/**
 * The Places browse filter, extracted so tests exercise the REAL predicate
 * instead of re-implementing it.
 *
 * This module exists because of a specific bug. A test asserting that our
 * own gym appears under Fitness was written against the fixture data — it
 * checked that the row carried an `exercise` tag. Reverting the actual
 * filter fix in PlacesPage left that test green, because the data was
 * still tagged; only the code that reads the tag had changed. A test that
 * cannot fail when the behaviour breaks is worse than no test, so the
 * logic now lives here and both the page and the tests call it.
 */

export type FilterablePlace = {
  category: string;
  tags: string[];
  slug?: string;
};

/**
 * Categories that also collect places whose PRIMARY category is something
 * else, via a tag.
 *
 * Every place has exactly one `category`, which is right for a browse list
 * — a place appearing twice with no way to tell why is confusing. But
 * "where can I exercise?" genuinely spans categories: Jack Peeples Park is
 * a park that happens to have twelve pickleball courts, Cypress Lodge is a
 * resident amenity centre with a heated pool, and the Field House is
 * primarily an emergency shelter that runs an open gym. Filing any of
 * those under `fitness` would have destroyed a more important meaning —
 * the Field House's hurricane-shelter role especially.
 *
 * So Fitness matches its own category OR an `exercise` tag. Places keep
 * their primary identity and still turn up when the question is where to
 * work out.
 */
export const TAG_CATEGORIES: Record<string, string> = {
  fitness: 'exercise',
};

export function matchesByTag(category: string, place: FilterablePlace): boolean {
  const tag = TAG_CATEGORIES[category];
  return !!tag && place.tags.includes(tag);
}

/**
 * Babcock National has a dedicated tab, so its rows stay out of the
 * general browse list under "All" — otherwise they appear twice with no
 * way to tell why. A specific chip like Fitness still surfaces them,
 * because the browse list is where the household would look for
 * "the pool we can walk to".
 *
 * The old "Babcock Ranch — our town" tab has been removed (its four
 * paragraphs of prose weren't earning their tab), so town-wide
 * babcock_ranch places no longer have a dedicated home. They come back
 * into the browse list where they belong -- Publix, Slater's,
 * Sunshine Ace and everything else in the retail corridor.
 */
export function hasDedicatedTab(place: FilterablePlace): boolean {
  return place.category === 'babcock_national';
}

/**
 * Which places the browse list shows for a given filter chip.
 *
 * `all` excludes anything with a dedicated tab. A specific chip includes
 * tag matches even when they have a dedicated tab: we live at Babcock
 * National, so its gym, lap pool and courts are the most usable exercise
 * venues we have, and hiding them from the Fitness filter would repeat
 * exactly the bug that filter was added to fix.
 */
export function selectByCategory<T extends FilterablePlace>(places: T[], category: string): T[] {
  if (category === 'all') return places.filter((p) => !hasDedicatedTab(p));
  return places.filter((p) => (p.category === category && !hasDedicatedTab(p)) || matchesByTag(category, p));
}

/**
 * Community fitness — options OUTSIDE Babcock National.
 *
 * The household lives at Babcock National, so the club gym / pool / courts
 * already have a dedicated tab. Chip asked for the town alternatives:
 * WellWay, Sweet Science, Jack Peeples Park, the Field House, and BRRA
 * Parks & Rec. Those are `babcock_ranch` community places, the same
 * tagging in-Ranch restaurants use — never `babcock_national`.
 *
 * The Places Fitness chip still includes National amenities (see
 * `selectByCategory`) because that list answers "where can I work out
 * today, including the gym we can walk to." Community-alts surfaces
 * (Favorites / curated lists) use this predicate so National-only
 * amenities are not listed as town alternatives.
 */
export function isCommunityFitnessPlace(place: FilterablePlace): boolean {
  if (place.category === 'babcock_national') return false;
  return place.category === 'fitness' || matchesByTag('fitness', place);
}

/** CoS-gathered community alts. Shown first on community fitness lists. */
export const COMMUNITY_FITNESS_FEATURED_SLUGS = [
  'wellway-babcock-ranch',
  'sweet-science-boxing-babcock-ranch',
  'jack-peeples-park',
  'babcock-high-school-field-house',
  'brra-parks-and-recreation',
] as const;

const FEATURED_COMMUNITY_FITNESS = new Set<string>(COMMUNITY_FITNESS_FEATURED_SLUGS);

/**
 * Prefer the five community alts Chip asked for, then the rest of the
 * in-Ranch exercise list, capped so a shortlist stays short.
 */
export function pickCommunityFitness<T extends FilterablePlace & { slug: string }>(
  places: T[],
  limit: number,
): T[] {
  const community = places.filter(isCommunityFitnessPlace);
  const featured: T[] = [];
  for (const slug of COMMUNITY_FITNESS_FEATURED_SLUGS) {
    const hit = community.find((p) => p.slug === slug);
    if (hit) featured.push(hit);
  }
  const rest = community.filter((p) => !FEATURED_COMMUNITY_FITNESS.has(p.slug));
  return [...featured, ...rest].slice(0, limit);
}
